import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import notify, { AppToastContainer } from "../../components/ACS-Dashboard/ACS-Blocks/Toast";
import Cookies from "js-cookie";
import Loader from "../../components/ACS-Dashboard/ACS-Blocks/Loader";
import ascendLogo from "../../assets/ACS-Images/Ascend-Logo.png";
import testimonialImage from "../../assets/ACS-Images/logistics-testimonial.png";
import { resetReduxState } from "../../redux/store";
import { getPostLoginRedirect } from "../../routes/dashboardPaths";

const VerifyOTP = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { email, expiresInSeconds, rememberMe } = location.state || {};

    const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [timeLeft, setTimeLeft] = useState(expiresInSeconds || 600);
    const inputRefs = useRef([]);

    // Redirect to login if no email in state (direct URL access)
    useEffect(() => {
        if (!email) {
            navigate("/login");
        }
    }, [email, navigate]);

    // Countdown timer
    useEffect(() => {
        if (timeLeft <= 0) return;
        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [timeLeft]);
    
    // Handle Enter key to submit
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === "Enter" && otpDigits.join("").length === 6 && timeLeft > 0 && !loading) {
                handleVerify();
            }
        };
        window.addEventListener("keydown", handleKeyPress);
        return () => window.removeEventListener("keydown", handleKeyPress);
    }, [otpDigits, timeLeft, loading]);

    // Format seconds to MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    // Handle individual digit input
    const handleDigitChange = (index, value) => {
        if (!/^\d*$/.test(value)) return; // only allow numbers

        const newDigits = [...otpDigits];
        newDigits[index] = value.slice(-1); // only keep last digit
        setOtpDigits(newDigits);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle backspace to go to previous input
    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").trim();
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split("");
            setOtpDigits(digits);
            inputRefs.current[5]?.focus();
        }
    };

    // Complete login after OTP verification (same logic as LoginPage)
    const completeLogin = async (loginData) => {
        try {
            // Fetch privileges
            const privilegeRes = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/auth/user/getPrivilegesByType?privilegeType=UI`,
                {
                    headers: {
                        Authorization: `Bearer ${loginData.accessToken}`,
                        Accept: "application/json",
                    },
                    withCredentials: true,
                }
            );

            const rawPrivileges = privilegeRes.data;
            localStorage.setItem("userPrivileges", JSON.stringify(rawPrivileges));

            const allowedMenus = rawPrivileges
                .filter((priv) => {
                    if (priv.action !== "VIEW") return false;
                    if (typeof priv.menu === "object") return priv.menu.active === true;
                    if (typeof priv.menu === "number") return true;
                    return false;
                })
                .map((priv) => {
                    if (typeof priv.menu === "object") return priv.menu.name;
                    return priv.name?.split(":")[0];
                })
                .filter(Boolean);

            localStorage.setItem("allowedMenus", JSON.stringify(allowedMenus));

            // Build menu tab structure
            function buildMenuTabStructure(privileges) {
                const menus = {};
                privileges.forEach(priv => {
                    const menu = priv.menu;
                    const parent = menu.parentMenu;
                    if (!parent) {
                        if (!menus[menu.name]) {
                            menus[menu.name] = {
                                displayName: menu.displayName,
                                icon: menu.icon,
                                tabs: {},
                                privilegeNames: [],
                                actions: []
                            };
                        }
                        if (!menus[menu.name].privilegeNames.includes(priv.name)) {
                            menus[menu.name].privilegeNames.push(priv.name);
                        }
                        if (!menus[menu.name].actions.includes(priv.action)) {
                            menus[menu.name].actions.push(priv.action);
                        }
                    } else {
                        const parentName = parent.name;
                        if (!menus[parentName]) {
                            menus[parentName] = {
                                displayName: parent.displayName,
                                icon: parent.icon,
                                tabs: {},
                                privilegeNames: [],
                                actions: []
                            };
                        }
                        if (!menus[parentName].tabs[menu.name]) {
                            menus[parentName].tabs[menu.name] = {
                                displayName: menu.displayName,
                                icon: menu.icon,
                                privilegeNames: [],
                                actions: []
                            };
                        }
                        if (!menus[parentName].tabs[menu.name].privilegeNames.includes(priv.name)) {
                            menus[parentName].tabs[menu.name].privilegeNames.push(priv.name);
                        }
                        if (!menus[parentName].tabs[menu.name].actions.includes(priv.action)) {
                            menus[parentName].tabs[menu.name].actions.push(priv.action);
                        }
                    }
                });
                return menus;
            }

            const menuTabStructure = buildMenuTabStructure(rawPrivileges);
            localStorage.setItem("menuTabStructure", JSON.stringify(menuTabStructure));
        } catch (err) {
            console.warn("Privilege fetch failed:", err);
        }

        // Set auth cookies
        Cookies.set("authToken", loginData.accessToken, {
            domain: process.env.REACT_APP_COOKIE_DOMAIN,
            expires: rememberMe ? 30 : 1,
            secure: process.env.REACT_APP_COOKIE_SECURE === "true",
            sameSite: "Lax",
        });

        Cookies.set("userData", JSON.stringify({
            id: loginData.id,
            email: loginData.email,
            name: loginData.name
        }), {
            domain: process.env.REACT_APP_COOKIE_DOMAIN,
            secure: process.env.REACT_APP_COOKIE_SECURE === "true",
            sameSite: "Lax",
        });

        // Fetch organizations
        let orgData = [];
        try {
            const orgResponse = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/auth/user/getOrganizations`,
                {
                    headers: {
                        Authorization: `Bearer ${loginData.accessToken}`,
                        Accept: "application/json",
                    },
                    withCredentials: true,
                }
            );
            orgData = orgResponse.data;
        } catch (orgError) {
            console.warn("Organization fetch warning:", orgError);
        }

        if (orgData.length > 0) {
            localStorage.setItem("organizationData", JSON.stringify(orgData));
            const orgTypes = Array.from(
                new Set(orgData.map(org => org.orgType?.toLowerCase()).filter(Boolean))
            );
            localStorage.setItem("organizationTypes", JSON.stringify(orgTypes));
            localStorage.setItem("officeCode", orgData[0].officeCode);

            Cookies.set("officeCode", orgData[0].officeCode, {
                domain: process.env.REACT_APP_COOKIE_DOMAIN,
                expires: rememberMe ? 30 : 1,
                secure: process.env.REACT_APP_COOKIE_SECURE === "true",
                sameSite: "Lax",
            });

            const officeCodes = orgData.map(org => org.officeCode).join(",");
            Cookies.set("officeCodes", officeCodes, {
                domain: process.env.REACT_APP_COOKIE_DOMAIN,
                expires: rememberMe ? 30 : 1,
                secure: process.env.REACT_APP_COOKIE_SECURE === "true",
                sameSite: "Lax",
            });
        }

        // Parse JWT expiry
        try {
            const payload = JSON.parse(atob(loginData.accessToken.split(".")[1]));
            if (payload.exp) {
                localStorage.setItem("sessionExpiry", (payload.exp * 1000).toString());
            }
        } catch (e) {
            console.warn("Could not parse JWT expiry:", e);
        }

        resetReduxState();
        navigate(getPostLoginRedirect(location.state?.from), { replace: true });
    };

    // Verify OTP
    const handleVerify = async () => {
        const otpCode = otpDigits.join("");
        if (otpCode.length !== 6) {
            notify.error({ title: "Invalid code", message: "Please enter all 6 digits." });
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/auth/user/verify-otp`,
                null,
                {
                    params: { email, otpCode },
                    headers: { "Content-Type": "application/json" },
                }
            );

            const loginData = response.data;
            await completeLogin(loginData);
        } catch (error) {
            const errorMessage = error.response?.data?.content
                || error.response?.data?.message
                || "Verification failed. Please try again.";
            notify.error({ title: "Verification failed", message: errorMessage });
            setOtpDigits(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    // Resend OTP
    const handleResend = async () => {
        if (resending) return;
        setResending(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/auth/user/resend-otp`,
                null,
                {
                    params: { email },
                    headers: { "Content-Type": "application/json" },
                }
            );
            setTimeLeft(response.data.expiresInSeconds || 600);
            setOtpDigits(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
            notify.success({ title: "Code resent", message: "A new verification code has been sent to your email." });
        } catch (error) {
            const errorMessage = error.response?.data?.content
                || error.response?.data?.message
                || "Failed to resend code.";
            notify.error({ title: "Resend failed", message: errorMessage });
        } finally {
            setResending(false);
        }
    };

    if (!email) return null;

    return (
        <div className="flex flex-col md:flex-row min-h-screen w-full bg-white overflow-x-hidden">
            {/* Left Side - Same testimonial image as login */}
            <div
                className="hidden md:block w-full md:w-1/2 h-screen bg-cover bg-center bg-no-repeat fixed"
                style={{ backgroundImage: `url(${testimonialImage})` }}
            />

            {/* Right Side - OTP Verification */}
            <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-4 xs:p-5 sm:p-6 ml-0 md:ml-[50%] bg-white dark:bg-acs-dark-surface">
                <div className="max-w-[90vw] xs:max-w-[85vw] sm:max-w-sm text-gray-900 dark:text-acs-dark-text">

                    {/* Logo */}
                    <div className="text-center space-y-fluid-2">
                        <img
                            src={ascendLogo}
                            alt="Ascend Logo"
                            className="w-[clamp(100px,15vw,160px)] mx-auto mb-fluid-2 transition-transform duration-300 hover:scale-105"
                        />
                        <h1 className="text-fluid-xl font-bold leading-tight">Verify Your Identity</h1>
                        <p className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
                            We sent a 6-digit code to <strong>{email}</strong>
                        </p>
                    </div>

                    {/* OTP Input */}
                    <div className="mt-8">
                        <div className="flex justify-center gap-3" onPaste={handlePaste}>
                            {otpDigits.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleDigitChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg
                                        bg-white dark:bg-acs-dark-surface
                                        border-gray-300 dark:border-acs-dark-border
                                        text-gray-900 dark:text-acs-dark-text
                                        focus:border-[#223688] dark:focus:border-[#31C4AB]
                                        focus:ring-2 focus:ring-[#223688] dark:focus:ring-[#31C4AB]
                                        focus:outline-none transition-colors"
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>

                        {/* Countdown Timer */}
                        <div className="text-center mt-4">
                            {timeLeft > 0 ? (
                                <p className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
                                    Code expires in{" "}
                                    <span className={`font-bold ${timeLeft <= 60 ? "text-red-500" : "text-[#223688] dark:text-[#31C4AB]"}`}>
                                        {formatTime(timeLeft)}
                                    </span>
                                </p>
                            ) : (
                                <p className="text-fluid-sm text-red-500 font-medium">
                                    Code has expired. Please request a new one.
                                </p>
                            )}
                        </div>

                        {/* Verify Button */}
                        <button
                            onClick={handleVerify}
                            disabled={loading || otpDigits.join("").length !== 6 || timeLeft <= 0}
                            className="w-full mt-6 p-2.5 text-sm rounded-lg font-medium
                                bg-[#223688] dark:bg-[#31C4AB]
                                text-white dark:text-[#1A1C23]
                                hover:bg-[#1c2c6d] dark:hover:bg-[#27b099]
                                disabled:opacity-50 disabled:cursor-not-allowed
                                flex justify-center items-center gap-2"
                        >
                            {loading ? (
                                <Loader compact message="Verifying..." color="text-white dark:text-white" />
                            ) : (
                                "Verify Code"
                            )}
                        </button>

                        {/* Resend Code */}
                        <div className="text-center mt-4">
                            <button
                                onClick={handleResend}
                                disabled={resending}
                                className="text-sm text-[#223688] dark:text-[#3B5CCC] hover:underline disabled:opacity-50"
                            >
                                {resending ? "Sending..." : "Didn't receive the code? Resend"}
                            </button>
                        </div>

                        {/* Back to Login */}
                        <div className="text-center mt-6">
                            <button
                                onClick={() => navigate("/login")}
                                className="text-sm text-gray-500 dark:text-acs-dark-subtext hover:underline"
                            >
                                ← Back to Sign In
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <AppToastContainer />
        </div>
    );
};

export default VerifyOTP;